import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { AppShell } from "./components/app-shell";
import { ErrorNotice, LoadingView } from "./components/feedback";
import { ADMIN_ROUTES } from "./constants";
import { SignInScreen } from "./features/auth/sign-in-screen";
import { AdminApiError, requestAdminApi } from "./lib/api-client";
import { authClient } from "./lib/auth-client";
import type { AdminSession } from "./types";

/**
 * Codocolle管理画面
 *
 * @returns 管理画面
 */
export const App = () => {
  // 現在のパスから選択中の管理機能を判定する
  const pathname = useRouterState({
    select: (state) => {
      return state.location.pathname;
    },
  });
  const activeSection = pathname.startsWith(ADMIN_ROUTES.push) ? "push" : "support";

  // 管理者セッションを保持する
  const [session, setSession] = useState<AdminSession | null>(null);

  // セッション確認の完了状態を保持する
  const [isSessionReady, setIsSessionReady] = useState(false);

  // セッション確認エラーを保持する
  const [sessionError, setSessionError] = useState<string | null>(null);

  // サインアウト処理中かを保持する
  const [isSigningOut, setIsSigningOut] = useState(false);

  // TanStack Routerの画面遷移処理を取得する
  const navigate = useNavigate();

  // 管理者セッションを確認する
  const loadSession = useCallback(async () => {
    setSessionError(null);

    try {
      const result = await requestAdminApi<AdminSession>("/v1/admin/session");
      setSession(result);
    } catch (error: unknown) {
      if (error instanceof AdminApiError && (error.status === 401 || error.status === 403)) {
        setSession(null);
      } else {
        console.error("Failed to load admin session", error);
        setSessionError("管理画面へ接続できませんでした。時間をおいてもう一度お試しください。");
      }
    } finally {
      setIsSessionReady(true);
    }
  }, []);

  // 管理機能間を移動する
  const handleNavigate = useCallback(
    (nextPath: typeof ADMIN_ROUTES.support | typeof ADMIN_ROUTES.push) => {
      void navigate({ to: nextPath });
      window.scrollTo({ top: 0 });
    },
    [navigate],
  );

  // ルートパスへ戻る
  const navigateToRoot = useCallback(() => {
    void navigate({ replace: true, to: ADMIN_ROUTES.root });
    window.scrollTo({ top: 0 });
  }, [navigate]);

  // サインアウトして認証画面へ戻す
  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);

    try {
      await authClient.signOut();
      setSession(null);
      navigateToRoot();
    } catch (error: unknown) {
      console.error("Admin sign out failed", error);
      setSessionError("サインアウトできませんでした。もう一度お試しください。");
    } finally {
      setIsSigningOut(false);
    }
  }, [navigateToRoot]);

  // 初回表示時に管理者セッションを確認する
  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  if (!isSessionReady) {
    return (
      <main className="bootstrap-page">
        <LoadingView label="管理画面を準備しています" />
      </main>
    );
  }

  if (!session) {
    return (
      <>
        {sessionError ? (
          <div className="session-error">
            <ErrorNotice message={sessionError} onRetry={() => void loadSession()} />
          </div>
        ) : null}
        <SignInScreen />
      </>
    );
  }

  return (
    <AppShell
      activeSection={activeSection}
      session={session}
      isSigningOut={isSigningOut}
      onNavigate={handleNavigate}
      onSignOut={() => void handleSignOut()}
    >
      {sessionError ? <ErrorNotice message={sessionError} /> : null}
      <Outlet />
    </AppShell>
  );
};
